<template>
  <div id="app" v-if="this.customSplashcreen">
    <loading
      :active.sync="pageLoading"
      :can-cancel="false"
      :is-full-page="true"
      loader="dots"
      background-color="rgba(0,0,0,.5)"
      color="white"
    ></loading>
    <CustomSplashscreen
      :timeout="this.customSplashcreen.timeout"
      :src="this.customSplashcreen.src"
      @splashscreen-complete="splashscreenComplete"
    />
    <v-dialog/>
    <modals-container/>
    <div v-show="this.isSplashscreenComplete">
      <Header/>
      <transition name="fade" mode="out-in">
        <router-view :direction="this.direction"/>
      </transition>
    </div>
    <CookiePopup/>
    <Dialogue/>
    <Appear2mee/>
    <HistoryStack/>
    <SharingComponent />
  </div>
</template>
<script>
import Appear2mee from './components/Appear2mee'
import CustomSplashscreen from './components/CustomSplashscreen.vue'
import Header from './components/Header'
import HistoryStack from './components/HistoryStack'
import Dialogue from './components/Dialogue'
import CookiePopup from './components/CookiePopup'
import SharingComponent from './components/SharingComponent'
import { mapState, mapActions } from 'vuex'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { spinnakerApi } from './js/SpinnakerApi.js'
import { DataConsentUtils } from './js/DataConsentUtils.js'
import { storage, REFRESH_TOKEN_TYPE_REGISTERED } from './js/Storage.js'
import { Workbox } from 'workbox-window'
import ScrollMonitor from './js/ScrollMonitor'

const dataConsentUtils = new DataConsentUtils(spinnakerApi)

let wb = null
if (navigator.serviceWorker) {
  if (process.env.NODE_ENV === 'production') {
    wb = new Workbox('/sw.js')
  } else {
    console.warn('Service Worker is not registered. Start app with `npm run service-worker`.')
  }
} else {
  console.info('Service Worker is not supported')
}

export default {
  name: 'app',
  components: {
    Appear2mee,
    Loading,
    CustomSplashscreen,
    Header,
    HistoryStack,
    SharingComponent,
    Dialogue,
    CookiePopup
  },
  data: function () {
    return {
      item: null,
      accessToken: null,
      page: 'home',
      direction: null,
      customSplashcreen: null,
      dataConsentInfo: null,
      isSplashscreenComplete: false,
      isCookiePopupShown: false,
      scrollMonitor: null
    }
  },
  computed: mapState({
    share: state => state.share,
    pageLoading: state => state.isLoading
  }),
  created: async function () {
    const splashScreenDetails = await spinnakerApi.getSplashScreen()
    const { splashScreenTimeout, splashScreenImage } = splashScreenDetails

    // Handle service worker updates
    if (wb) {
      // Add an event listener to detect when the registered
      // service worker has installed but is waiting to activate.
      wb.addEventListener('waiting', event => {
        this.showAppUpdateNotice(event)
      })
      wb.register()
    }

    this.customSplashcreen = {
      timeout:
        this.isRegistrationConfirmation() || this.isPasswordReset()
          ? 0
          : splashScreenTimeout.value,
      src: splashScreenImage.value
    }

    if (navigator.onLine) {
      // When splash screen is going to be displayed we are forcing fetching new category data in service worker
      if (this.customSplashcreen.timeout !== 0 && navigator.serviceWorker && navigator.serviceWorker.controller) {
        setTimeout(() => { navigator.serviceWorker.controller.postMessage('CLEAR_CATEGORY_CACHE') }, 0)
      }

      try {
        await spinnakerApi.autoSignIn()
        this.$store.commit('isSignedInUser', true)

        const accountSummary = await spinnakerApi.fetchAccountSummary()
        const isRegistered = await spinnakerApi.isAccountForRegisteredUser(
          accountSummary
        )
        this.dataConsentInfo = await dataConsentUtils.fetchDataConsentInfo(
          accountSummary
        )
        this.$store.commit('isRegisteredUser', isRegistered)
      } catch (err) {
        console.error(`Failed to get account information: ${err}`)
      }
    } else {
      const isOfflineButRegistered =
        storage.getRefreshTokenType() === REFRESH_TOKEN_TYPE_REGISTERED
      this.$store.commit('isSignedInUser', true)
      this.$store.commit('isRegisteredUser', isOfflineButRegistered)
    }

    if (this.isRegistrationConfirmation()) {
      this.$store.commit('startSignIn', 'REGISTRATION_CONFIRMED')
    } else if (this.isPasswordReset()) {
      this.$store.commit('performPasswordReset', this.$route.query.token)
    }

    this.showCookiePopupIfRequired()

    this.scrollMonitor = new ScrollMonitor((isScrollOngoing) => {
      console.info(`App: scroll ${isScrollOngoing}`)
      this.$store.commit('navigation/isScrollOngoing', isScrollOngoing)
    })
    window.addEventListener('scroll', this.scrollMonitor.isScrollOngoing)

    await this.$store.dispatch('start')
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.scrollMonitor.isScrollOngoing)
    document.documentElement.removeEventListener('touchstart', this.handleTouchStart)
  },
  mounted: async function () {
    document.documentElement.addEventListener(
      'touchstart',
      this.handleTouchStart,
      { passive: false }
    )
    this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
        case 'performAppUpdate':
          this.performAppUpdate()
          break
        case 'declineAppUpdate':
          this.declineAppUpdate()
          break
        case 'showLoading':
          document.documentElement.style.overflowY = 'hidden'
          break
        case 'hideLoading':
          document.documentElement.style.overflowY = 'visible'
          break
      }
    })
  },
  methods: {
    ...mapActions('share', ['getShareToken']),
    splashscreenComplete (event) {
      this.isSplashscreenComplete = true
      this.showCookiePopupIfRequired()
    },
    showCookiePopupIfRequired () {
      if (this.isCookiePopupShown) {
        return // already shown - nothing to do
      }
      if (!this.dataConsentInfo) {
        return // haven't got account info yet, don't know if already accepted - wait
      }
      if (this.dataConsentInfo.isConsentUpToDate) {
        return // data consent is up-to-date - nothing to do
      }
      if (!this.isSplashscreenComplete) {
        return // not ready to show data consent yet because splashscreen showing
      }
      if (this.isRegistrationConfirmation() || this.isPasswordReset()) {
        return // don't bother user just now if following an email link
      }

      this.$store.commit('showCookiePopup')
      this.isCookiePopupShown = true
    },
    isRegistrationConfirmation () {
      return location.href.indexOf('registration-confirmed=true') !== -1
    },
    isPasswordReset () {
      return location.href.indexOf('password-reset=true') !== -1
    },
    showError (message, title) {
      this.$modal.show('dialog', {
        title,
        text: message,
        buttons: [
          {
            title: 'Close'
          }
        ]
      })
    },
    handleTouchStart (e) {
      if (this.$store.state.isLoading) {
        e.preventDefault()
      }
    },
    showAppUpdateNotice (e) {
      console.info(
        `App.showAppUpdateNotice received notice of updated service worker event=${e}`
      )
      this.$store.commit('showAppUpdateNotice')
    },
    performAppUpdate () {
      if (wb) {
        console.info(`App.performAppUpdate - sending skipWaiting message`)
        wb.addEventListener('controlling', event => {
          console.info(`App.performAppUpdate - reloading`)
          window.location.reload()
        })

        // Send a message telling the service worker to skip waiting.
        // This will trigger the `controlling` event handler above.
        wb.messageSW({ type: 'SKIP_WAITING' })
      }
    },
    declineAppUpdate () {
      console.info(`App.declineAppUpdate - scheduling a reminder`)
      setTimeout(() => {
        this.$store.commit('showAppUpdateNotice')
      }, 60 * 60 * 1000)
    }
  }
}
</script>
